import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { CasestudyCard, casestudyCards } from ".";
import { CaseStudyHerosection, CaseStudyInformation, ClientFeedback, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const Pickyourtrail: React.FC = () => {
    return (
        <NewLayout
            title="Educenter | WhatsApp Business customer story"
            description="Find out how Educenter empowers student learning through Gallabox's contextual WhatsApp automation for onboarding, enquiries, and grading."
            canonical="https://gallabox.com/case-study/educenter"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/educenter-og.png" }}
        >
            <CaseStudyHerosection
                title="How Educenter used WhatsApp to empower students and increased revenue by 40%"
                description="Find out for Educenter empowers student learning through Gallabox's contextual WhatsApp automation for onboarding, enquiries, and grading."
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/educenter.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "40%", label: "Increase in business profits" },
                    { percentage: "80%", label: "Impact in overall business growth" },
                    { percentage: "35%", label: "Improvement in CSAT ratings" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[
                                { label: "Company", value: ["Educenter"] },
                                { label: "Industry", value: ["Education"] },
                                { label: "Employees", value: ["10-50"] },
                                { label: "Team", value: ["Sales", "Support", "Marketing"] },
                            ]}
                            product={{
                                title: "Features Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "Broadcast" },
                                    { link: "https://gallabox.com/whatsapp-flows", label: "Flows" },
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "Chatbots" },
                                    { link: "https://gallabox.com/click-to-whatsapp-ads", label: "Click-to-WhatsApp Ads" },
                                    { link: "https://gallabox.com/whatsapp-integrations", label: "Integrations" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About">
                            <CaseStudyInformation mainTitle={"Israel's trusted partner for educational training in both private and public sectors"} className="mt-0" />
                            <p className="mt-2">
                                The ability to communicate effectively with students is the first thing that inspires trust in{" "}
                                <a href="https://www.educenter.co.il/">The Center of Educational Training&apos;s</a> long journey of learning. For this progressive educational
                                institution, the goal was clear: how to provide top-notch support to students scattered across the country while keeping operations lean and
                                efficient. And that also means, training the instructors with the right resources to develop these students across age groups.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"Educenter was out to reimagine the entire student support experience"} className="mt-0" />
                            <p className="mt-2">
                                Operating across multiple locations, Educenter faced a unique challenge. With students spread throughout the country of Israel and instructors
                                managing multiple courses, they needed a solution that could bridge the distance while maintaining personal connections. &quot;We have students all
                                over the country,&quot; explains Omri Gonen, Founder, The Center for Educational Training, highlighting how traditional communication methods were
                                insufficient.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation
                                subTitle={"The communication conundrum was both a challenge and opportunity"}
                                descriptions={["Before their digital transformation, the institution grappled with several challenges:"]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "Managing widespread student communications" },
                                    { listItem: "Coordinating between instructors and students" },
                                    { listItem: "Processing certificates manually" },
                                    { listItem: "Handling grade inquiries" },
                                    { listItem: "Maintaining organized communication records" },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                            <p className="mt-5">
                                The certificate issuance process alone was a nightmare of manual steps, taking weeks to complete and consuming valuable staff time. &quot;We had
                                some person in our office check if the student passed the test, confirm the information in Google Forms, and then we had to call and issue the
                                certificate,&quot; he recalls, painting a picture of the tedious process they once endured.
                            </p>
                        </section>
                        <section>
                            <CaseStudyInformation subTitle={"Zeroing in on Gallabox after testing out five platforms"} className="mt-0" />
                            <p className="mt-2">
                                The search for the right solution was thorough. &quot;I signed up for like five platforms,&quot; Omri shares. &quot;I checked every platform on
                                Earth.&ldquo; After extensive evaluation, Gallabox emerged as the winner, offering the optimal balance of features and pricing. <br></br>
                                <br></br>The clincher was Gallabox&quot;s features. They seamlessly blended with everything Omri wanted to solve for:
                            </p>

                            <div className="bg-gray-100  border-solid border-l-2  border-black my-6">
                                <ul className="gap-y-2 py-2">
                                    <li className="list-none flex items-center gap-x-5">
                                        <IoIosCheckmarkCircle />
                                        Automated student onboarding with personalized{" "}
                                        <a href="https://gallabox.com/resources/free-whatsapp-message-templates">WhatsApp templates</a>
                                    </li>

                                    <li className="list-none flex items-center gap-x-5">
                                        <IoIosCheckmarkCircle />
                                        <a href="https://gallabox.com/whatsapp-chatbot">Bot-driven</a> grade distribution system
                                    </li>
                                    {["Structured instructor-student communication channels", "Automated certificate generation and distribution."].map((data) => (
                                        <li className="list-none flex items-center gap-x-5" key={`${data}`}>
                                            <IoIosCheckmarkCircle />
                                            {data}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <CaseStudyInformation subTitle={"Early results: Instant gratification with process automation"} className="mt-10" />
                            <p className="mt-2">
                                happens in minutes. &quot;When the exam is done, it triggers a webhook in Gallabox. The certificate is generated in the background and sent
                                automatically via Gallabox to the student.&quot;. Omri explained.This automation saved approximately 100 hours monthly a 50% reduction in staff time
                                for certificate processing alone.
                            </p>
                            <CaseStudyInformation subTitle={"Building a communication ecosystem around Gallabox"} className="mt-10" />
                            <p className="mt-2">
                                The institution took a bold stance: making WhatsApp their primary communication channel. &quot;We don&ldquo;t provide phone support unless we really
                                have to,&quot; says Omri. This decision wasn&ldquo;t about avoiding student contact-it was about making support more efficient and accessible.
                            </p>
                            <p className="mt-5">They integrated Gallabox with their entire ecosystem:</p>
                            <FeatureList
                                List={[
                                    { listItem: "Learning Management System (LMS)" },
                                    { listItem: "CRM system" },
                                    {
                                        listItem: "Dialer integration",
                                    },
                                    {
                                        listItem: "API connections for automated responses",
                                    },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                        </section>
                        <section id="Impact">
                            <CaseStudyInformation
                                subTitle={"The impact: Student engagement translates into revenue"}
                                descriptions={[
                                    "The enviable levels of student engagement through conversational chatbots propelled the performance metrics and overall business growth:",
                                ]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "50% improvement in employee productivity" },
                                    { listItem: "40% growth directly attributed to Gallabox" },
                                    { listItem: "Certificate processing time reduced from weeks to 10 minutes" },
                                    { listItem: "35% increase in student satisfaction" },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                        </section>
                        <section>
                            <CaseStudyInformation
                                subTitle={"Looking to the future"}
                                descriptions={["The institution isn't resting on its laurels. With a substantial marketing budget and ambitious plans, they're exploring:"]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "Enhanced conversational AI capabilities" },
                                    { listItem: "Expanded sales team integration" },
                                    { listItem: "Lead generation automation" },
                                    { listItem: "Click-to-WhatsApp advertising" },
                                    { listItem: "Marketing automation" },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                            <p className="mt-5">
                                The institution sees Gallabox as more than just a tool - it&apos;s a long-term partner in their growth journey. &ldquo;We do see our work with
                                Gallabox going forward many years,&quot; Omri shares, highlighting their commitment to this digital transformation path.
                                <br />
                                <br />
                                Their story serves as a blueprint for educational institutions looking to modernize their student support systems. It shows how the right
                                technology, implemented thoughtfully, can transform not just operations but the entire educational experience.
                            </p>

                            <ClientFeedback
                                descriptions={
                                    <p className="text-gray-900 italic font-semibold px-6 mb-4">
                                        When the exam is done, it triggers a webhook in Gallabox. The certificate is generated in the background and sent automatically via Gallabox
                                        to the student.
                                    </p>
                                }
                                authorName="Omri Gonen"
                                designationWithServiceName="Founder, The Center for Educational Training"
                                className="border-blue-500 bg-blue-100 mt-4 "
                            />
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <section className="max-w-6xl mx-auto">
                <div>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCards.slice(0, 3)?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </div>
            </section>
        </NewLayout>
    );
};

export default Pickyourtrail;
